import '../../styles/fonts.css';
import React, { useState } from 'react';

interface PixelBoxProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  shadowColor?: string;
  innerShadowColor?: string;
  innerHighlightColor?: string;
  isButton?: boolean;
  className?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  style?: React.CSSProperties;
}

export const PixelBox = ({
  style,
  children,
  onClick,
  textColor = '#000000',
  backgroundColor = '#E6E6E6',
  borderColor = '#000000',
  shadowColor = '#808080',
  innerShadowColor = '#AAAAAA',
  innerHighlightColor = '#FFFFFF',
  isButton = false,
  className = '',
  disabled = false,
  size = 'large',
}: PixelBoxProps) => {
  const [isPressed, setIsPressed] = useState(false);

  const sizeClasses = {
    small: 'px-6 py-3 text-sm',
    medium: 'px-8 py-4 text-base',
    large: 'px-8 py-5 text-lg',
    xlarge: 'px-10 py-6 text-xl',
  };

  const baseClasses = `
    relative inline-block
    ${sizeClasses[size]}
    font-mono font-bold uppercase tracking-wider
    select-none
    ${(isButton || onClick) && !disabled ? 'cursor-pointer' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `;

  const handleMouseDown = () => (isButton || onClick) && !disabled && setIsPressed(true);
  const handleMouseUp = () => (isButton || onClick) && !disabled && setIsPressed(false);
  const handleMouseLeave = () => (isButton || onClick) && !disabled && setIsPressed(false);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if ((isButton || onClick) && !disabled && onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }
  };

  const shouldBeButton = isButton || onClick;
  const Component = (shouldBeButton ? 'button' : 'div') as any;

  const pixelCornersClipPath = `polygon(
    0px calc(100% - 6px),
    3px calc(100% - 6px),
    3px calc(100% - 3px),
    6px calc(100% - 3px),
    6px 100%,
    calc(100% - 6px) 100%,
    calc(100% - 6px) calc(100% - 3px),
    calc(100% - 3px) calc(100% - 3px),
    calc(100% - 3px) calc(100% - 6px),
    100% calc(100% - 6px),
    100% 6px,
    calc(100% - 3px) 6px,
    calc(100% - 3px) 3px,
    calc(100% - 6px) 3px,
    calc(100% - 6px) 0px,
    6px 0px,
    6px 3px,
    3px 3px,
    3px 6px,
    0px 6px
)`;

  const pixelBorderClipPath = `polygon(
    0px calc(100% - 6px),
    3px calc(100% - 6px),
    3px calc(100% - 3px),
    9px calc(100% - 3px),
    9px 100%,
    calc(100% - 9px) 100%,
    calc(100% - 9px) calc(100% - 3px),
    calc(100% - 6px) calc(100% - 3px),
    calc(100% - 6px) calc(100% - 6px),
    100% calc(100% - 6px),
    100% 6px,
    calc(100% - 6px) 6px,
    calc(100% - 6px) 3px,
    calc(100% - 9px) 3px,
    calc(100% - 9px) 0px,
    9px 0px,
    9px 3px,
    6px 3px,
    6px 6px,
    0px 6px
)`;

  const innerShadowClipPath = `polygon(
    calc(100% - 6px) 6px,
    calc(100% - 3px) 6px,
    calc(100% - 3px) calc(100% - 6px),
    6px calc(100% - 6px),
    6px calc(100% - 3px),
    calc(100% - 6px) calc(100% - 3px),
    calc(100% - 6px) 6px
  )`;

  const innerHighlightClipPath = `polygon(
    6px 6px,
    calc(100% - 6px) 6px,
    calc(100% - 6px) 9px,
    9px 9px,
    9px calc(100% - 6px),
    6px calc(100% - 6px),
    6px 6px
  )`;

  const pixelStyle: React.CSSProperties = {
    backgroundColor,
    color: textColor,
    position: 'relative',
    outline: 'none',
    border: 'none',
    clipPath: pixelCornersClipPath,
    imageRendering: 'pixelated' as any,
    WebkitFontSmoothing: 'none' as any,
    MozOsxFontSmoothing: 'unset' as any,
    textRendering: 'geometricPrecision' as any,
    transform: isPressed ? 'translate(2px, 2px)' : 'translate(0, 0)',
    transition: 'transform 0.1s ease',
    boxShadow: isPressed
      ? 'none'
      : `
        2px 0 0 0 ${borderColor},
        0 2px 0 0 ${borderColor},
        2px 2px 0 0 ${borderColor},
        4px 2px 0 0 ${borderColor},
        2px 4px 0 0 ${borderColor},
        4px 4px 0 0 ${borderColor}
      `,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    ...style,
  };

  const borderStyle: React.CSSProperties = {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    clipPath: pixelBorderClipPath,
    backgroundColor: '#000000',
    zIndex: 0,
    pointerEvents: 'none',
    margin: '-3px',
    marginBottom: '-5px',
    marginRight: '-4px',
  };

  const innerShadowStyle: React.CSSProperties = {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -4,
    bottom: -4,
    clipPath: innerShadowClipPath,
    backgroundColor: innerShadowColor,
    zIndex: 2,
    pointerEvents: 'none',
  };

  const innerHighlightStyle: React.CSSProperties = {
    content: '""',
    position: 'absolute',
    top: -6,
    left: -6,
    right: 0,
    bottom: 0,
    clipPath: innerHighlightClipPath,
    backgroundColor: innerHighlightColor,
    zIndex: 2,
    pointerEvents: 'none',
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div style={borderStyle} />
      <Component
        className={baseClasses}
        style={pixelStyle}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        disabled={shouldBeButton ? disabled : undefined}
        type={shouldBeButton ? 'button' : undefined}
      >
        {children}
        <div style={innerShadowStyle} />
        <div style={innerHighlightStyle} />
      </Component>
    </div>
  );
};

interface PixelButtonProps extends PixelBoxProps {}
export const PixelButton: React.FC<PixelButtonProps> = (props) => <PixelBox {...props} isButton />;

interface PixelContainerProps extends PixelBoxProps {}
export const PixelContainer: React.FC<PixelContainerProps> = (props) => <PixelBox {...props} isButton={false} />;
